import { consume } from "@lit/context";
import {
    type DocumentDTO,
    type DocumentOrigin,
    type DocumentTagDTO,
    type Employee,
    employeeNameOrderForSort,
    type EmployeeSortProperty,
    fileIcon,
    type Form,
    formatDate,
    formStatusColor,
    formStatusLabel,
    getDefaultForms,
    GetDocumentsParams,
    getEmployeeName,
    MimeType,
    toDateString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import "../../../components/documents/document-dialog";
import type { DocumentDialog } from "../../../components/documents/document-dialog";
import {
    documentMatchesOrigins,
    documentOriginMeta,
    documentOriginOptions,
} from "../../../components/documents/document-origin";
import "../../../components/documents/document-origin-pill";
import "../../../components/documents/edit-document-dialog";
import type { EditDocumentDialog } from "../../../components/documents/edit-document-dialog";
import { type EnabledFeatures, enabledFeatures } from "../../../contexts/features-context";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/popover";
import "../../../elements/scroller";
import { app } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { selectFile } from "../../../lib/util";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../employees-filter";

@customElement("ptc-employees-documents-row")
export class EmployeesDocumentsRow extends LitElement {
    @property({ type: Boolean })
    isVisible = false;

    @property({ attribute: false })
    employee: Employee;

    @property({ attribute: false })
    documents: DocumentDTO[] = [];

    shouldUpdate(changes: Map<string, any>) {
        return changes.has("isVisible") || this.isVisible;
    }

    createRenderRoot() {
        return this;
    }

    render() {
        return html`${cache(this.isVisible ? this._render() : "")}`;
    }

    private _render() {
        return html`
            <div
                class="margined center-aligning spacing horizontal layout hover-highlight"
                @click=${() => this.dispatchEvent(new CustomEvent("open-employee"))}
            >
                <ptc-ui-avatar
                    class="tinier"
                    .employee=${this.employee}
                    .positionColors=${this.employee.positions.map((p) => app.company.getPositionColor(p))}
                ></ptc-ui-avatar>
                <div>${getEmployeeName(this.employee, employeeNameOrderForSort(app.employeeFilters.sortProperty))}</div>
            </div>

            <div class="margined horizontal scroller">
                <div class="spacing horizontal layout">
                    ${this.documents.map(
                        (document) => html`
                            <div
                                class="right-padded click horizontal center-aligning layout rounded"
                                @click=${() =>
                                    this.dispatchEvent(new CustomEvent("open-document", { detail: { document } }))}
                            >
                                <ptc-ui-icon name="${fileIcon(document.type)}" class="massive"></ptc-ui-icon>
                                <div>
                                    <div class="small semibold ellipsis bottom-margined" style="max-width: 10em;">
                                        ${document.name}
                                    </div>
                                    <div class="tiny horizontal spacing layout">
                                        <div class="pill">
                                            <ptc-ui-icon name="icon.object.date"></ptc-ui-icon> ${formatDate(
                                                document.date,
                                                false
                                            )}
                                        </div>
                                        <ptc-document-origin-pill .document=${document}></ptc-document-origin-pill>
                                        ${document.hideFromEmployee
                                            ? html`
                                                  <div class="pill">
                                                      <ptc-ui-icon name="icon.toggle.visibility-hidden"></ptc-ui-icon>
                                                  </div>
                                              `
                                            : ""}
                                        ${document.tags.length > 1
                                            ? html`
                                                  <div class="pill ellipsis" style="max-width: 100%">
                                                      <ptc-ui-icon name="icon.object.category"></ptc-ui-icon>
                                                      ${document.tags.length} Tags
                                                  </div>
                                              `
                                            : document.tags.map(
                                                  (tag) => html`
                                                      <div
                                                          class="pill ellipsis"
                                                          style="max-width: 100%; --color-highlight: ${tag.color}"
                                                      >
                                                          <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon> ${tag.name}
                                                      </div>
                                                  `
                                              )}
                                        ${document.form
                                            ? html`<div
                                                  class="pill"
                                                  style="--color-highlight: ${formStatusColor(document.form.status)}"
                                              >
                                                  <ptc-ui-icon name="icon.object.form"></ptc-ui-icon> ${formStatusLabel(
                                                      document.form.status
                                                  )}
                                              </div>`
                                            : ""}
                                    </div>
                                </div>
                            </div>
                        `
                    )}
                    <button class="subtle" @click=${() => this.dispatchEvent(new CustomEvent("new-document"))}>
                        <div class="center-aligning horizontal layout" style="width: 11.5em">
                            <ptc-ui-icon name="icon.action.upload" class="huge"></ptc-ui-icon>
                            <div class="tiny stretch left-margined text-left-aligning">
                                Datei wählen oder per <strong>Drag & Drop</strong> hier ablegen.
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        `;
    }
}

@customElement("ptc-employees-documents-all")
export class EmployeesDocumentsAll extends Routing(StateMixin(LitElement)) {
    routePattern = /employees\/all\/documents/;

    get routeTitle() {
        return "Dokumente: Alle Mitarbeiter";
    }

    @state()
    private _documents: DocumentDTO[] = [];

    @state()
    private _loading: boolean = false;

    @state()
    private _selectedTag: DocumentTagDTO | null = null;

    @state()
    private _documentTags: DocumentTagDTO[] = [];

    @state()
    private _activeOrigins: Set<DocumentOrigin> = new Set(documentOriginOptions);

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures?: EnabledFeatures;

    @singleton("ptc-edit-document-dialog")
    private _editDocumentDialog: EditDocumentDialog;

    @singleton("ptc-document-dialog")
    private _documentDialog: DocumentDialog;

    handleRoute() {
        // update in the background
        void this._load();
    }

    private get _employees() {
        return app
            .getFilteredEmployees()
            .filter((e) =>
                this._documents.some(
                    (d) =>
                        d.employeeId === e.id &&
                        documentMatchesOrigins(d, this._activeOrigins) &&
                        (!this._selectedTag || d.tags.some((tag) => tag.id === this._selectedTag?.id))
                )
            );
    }

    private _matchesFilters(document: DocumentDTO) {
        return (
            documentMatchesOrigins(document, this._activeOrigins) &&
            (!this._selectedTag || document.tags.some((tag) => tag.id === this._selectedTag?.id))
        );
    }

    private _toggleOrigin(origin: DocumentOrigin) {
        const next = new Set(this._activeOrigins);
        if (next.has(origin)) {
            if (next.size === 1) {
                return;
            }
            next.delete(origin);
        } else {
            next.add(origin);
        }
        this._activeOrigins = next;
    }

    private _intersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => this._intersectionHandler(entries),
        { root: this, rootMargin: "100%" }
    );

    private _intersectionHandler(entries: IntersectionObserverEntry[]) {
        entries.forEach((e) => ((e.target as EmployeesDocumentsRow).isVisible = e.isIntersecting));
    }

    updated() {
        const elements = this.renderRoot.querySelectorAll("ptc-employees-documents-row");
        for (const el of elements) {
            this._intersectionObserver.observe(el);
        }
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener("dragenter", () => this.classList.add("dragging"));
        this.addEventListener("dragend", () => this.classList.remove("dragging"));
        this.addEventListener("dragover", (e: DragEvent) => e.preventDefault());
        this.addEventListener("drop", (e: DragEvent) => {
            e.preventDefault();
            const file = e.dataTransfer?.files[0];
            void this._newDocument({ file });
            this.classList.remove("dragging");
        });
        this.addEventListener("dragleave", (e: DragEvent) => {
            if (e.target === this) {
                this.classList.remove("dragging");
            }
        });
    }

    private async _load() {
        this._loading = true;
        try {
            const [documentsPromise, documentTagsPromise] = await Promise.allSettled([
                app.api.getDocuments(new GetDocumentsParams()),
                app.api.getDocumentTagsForCompany(),
            ]);
            this._documents = documentsPromise.status === "fulfilled" ? documentsPromise.value : [];
            this._documentTags = documentTagsPromise.status === "fulfilled" ? documentTagsPromise.value : [];
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _newDocument({ file, form, employee }: { file?: File; form?: Form; employee?: Employee } = {}) {
        const document = {
            employeeId: employee?.id || 0,
            date: toDateString(new Date()),
            tags: this._selectedTag ? [this._selectedTag] : [],
            form: form ?? null,
            name: form?.name || file?.name || "",
            type: form ? MimeType.PDF : undefined,
            comment: "",
        };

        const created = await this._editDocumentDialog.show({ file, document });
        if (created) {
            // update in the background
            void this._load();
            void app.fetchCompany();

            await this._openDocument(created);
        }
    }

    private async _openDocument(doc: DocumentDTO) {
        const edited = await this._documentDialog.show(doc, { showActions: true });
        if (edited) {
            // update in the background
            void this._load();
            void app.fetchCompany();
        }
    }

    private _dragover(e: DragEvent) {
        e.preventDefault();

        if (!!e.dataTransfer && [...e.dataTransfer.types].includes("Files")) {
            e.dataTransfer.dropEffect = "copy";
        }
    }

    private _drop(e: DragEvent, employee: Employee) {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer?.files[0];

        void this._newDocument({ employee, file });

        const el = e.target as EmployeesDocumentsRow;
        el.classList.remove("dragover");
        this.classList.remove("dragging");
    }

    private _dragenter(e: DragEvent) {
        e.preventDefault();
        const el = e.target as EmployeesDocumentsRow;
        el.classList.toggle("dragover", !!e.dataTransfer && [...e.dataTransfer.types].includes("Files"));
    }

    private _dragleave(e: DragEvent) {
        e.preventDefault();
        const el = e.target as EmployeesDocumentsRow;
        el.classList.remove("dragover");
    }

    private _dragend(e: DragEvent) {
        e.preventDefault();
        const el = e.target as EmployeesDocumentsRow;
        el.classList.remove("dragover");
        this.classList.remove("dragging");
    }

    static styles = [
        shared,
        css`
            :host(.dragging) ptc-employees-documents-row > * {
                pointer-events: none;
            }

            ptc-employees-documents-row {
                display: block;
                min-height: 5em;
            }

            ptc-employees-documents-row.dragover button,
            ptc-employees-documents-row.dragover .hover-highlight {
                color: var(--color-primary);
            }
        `,
    ];

    private _renderNewDocumentOptions() {
        return html`
            <ptc-popover class="popover-menu" hide-on-click>
                ${getDefaultForms(app.company!).map(
                    (form) => html`
                        <button type="button" class="small" @click=${() => this._newDocument({ form })}>
                            <ptc-ui-icon name="icon.object.form"></ptc-ui-icon> ${form.name}
                        </button>
                    `
                )}

                <button
                    type="button"
                    class="small"
                    @click=${async () => {
                        const file = await selectFile();
                        if (file) {
                            await this._newDocument({ file });
                        }
                    }}
                >
                    <ptc-ui-icon name="icon.action.choose-file"></ptc-ui-icon> Datei Wählen...
                </button>
            </ptc-popover>
        `;
    }

    render() {
        const filteredDocuments = this._documents.filter((doc) => this._matchesFilters(doc));
        return html`
            <div class="fullbleed vertical layout wrapper">
                <div class="padded spacing center-aligning horizontal layout border-bottom noprint">
                    <div class="stretch"></div>

                    <button class="large skinny transparent">
                        <ptc-ui-icon name="icon.action.upload"></ptc-ui-icon>
                    </button>

                    ${this._renderNewDocumentOptions()}
                </div>

                ${this._enabledFeatures?.documentOriginFilter
                    ? html`
                          <div class="small padded vertical layout border-bottom">
                              <div class="tiny subtle semibold bottom-margined center-aligning horizontal layout">
                                  <ptc-ui-icon name="icon.object.source" class="right-margined"></ptc-ui-icon> Quelle
                              </div>
                              <div class="spacing wrapping horizontal layout">
                                  ${documentOriginOptions.map(
                                      (origin) => html`
                                          <button
                                              class="slim ${this._activeOrigins.has(origin)
                                                  ? "primary"
                                                  : "transparent"}"
                                              @click=${() => this._toggleOrigin(origin)}
                                          >
                                              <ptc-ui-icon name="${documentOriginMeta[origin].icon}"></ptc-ui-icon>
                                              ${documentOriginMeta[origin].label}
                                          </button>
                                      `
                                  )}
                              </div>
                          </div>
                      `
                    : ""}

                <div class="small padded vertical layout border-bottom">
                    <div class="tiny subtle semibold bottom-margined center-aligning horizontal layout">
                        <ptc-ui-icon name="icon.object.category" class="right-margined"></ptc-ui-icon> Kategorien
                    </div>
                    <div class="spacing wrapping center-aligning horizontal layout">
                        <button
                            class="slim ${!this._selectedTag ? "primary" : "transparent"}"
                            @click=${() => (this._selectedTag = null)}
                        >
                            <ptc-ui-icon name="icon.object.category"></ptc-ui-icon> Alle
                            <span class="subtle" style="display: inline-block; margin-left: 0.25em;">
                                ${this._documents.length}
                            </span>
                        </button>
                        ${this._documentTags?.map(
                            (tag) => html`
                                <button
                                    class="slim ${this._selectedTag?.id === tag.id ? "primary" : "transparent"}"
                                    @click=${() => (this._selectedTag = tag)}
                                    style="--color-highlight: ${tag.color}"
                                >
                                    <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon> ${tag.name}
                                    <span class="subtle" style="display: inline-block; margin-left: 0.25em;">
                                        ${this._documents.filter((doc) => doc.tags.some((t) => t.name === tag.name))
                                            .length}
                                    </span>
                                </button>
                            `
                        )}
                        <div class="stretch"></div>
                        <button
                            class="subtle"
                            @click=${() => this.go("settings/documents")}
                            ?disabled=${!app.hasPermission("manage.settings.documents")}
                        >
                            <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon> Kategorien Bearbeiten
                        </button>
                    </div>
                </div>

                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
                ></ptc-employees-filter>

                ${!this._employees.length
                    ? html`
                          <div class="stretch centering vertical layout">
                              <ptc-ui-icon name="icon.file.collection" class="giant faded"></ptc-ui-icon>
                              <div class="margined">Dieser Mitarbeiter hat noch keine Dokumente.</div>
                              <button class="subtle top-margined">
                                  <div class="center-aligning horizontal layout" style="width: 11.5em">
                                      <ptc-ui-icon name="icon.action.upload" class="huge"></ptc-ui-icon>
                                      <div class="tiny stretch left-margined text-left-aligning">
                                          Datei wählen oder per <strong>Drag & Drop</strong> hier ablegen.
                                      </div>
                                  </div>
                              </button>

                              ${this._renderNewDocumentOptions()}
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch">
                              ${this._employees.map(
                                  (employee) => html`
                                      <ptc-employees-documents-row
                                          class="border-bottom"
                                          .employee=${employee}
                                          .documents=${filteredDocuments.filter(
                                              (doc) => doc.employeeId === employee.id
                                          )}
                                          @open-employee=${() => this.go(`employees/${employee.id}/documents`)}
                                          @open-document=${(e: CustomEvent<{ document: DocumentDTO }>) =>
                                              this._openDocument(e.detail.document)}
                                          @new-document=${(e: CustomEvent<{ file?: File }>) =>
                                              this._newDocument({ employee, file: e.detail?.file })}
                                          @dragover=${this._dragover}
                                          @drop=${(e: DragEvent) => this._drop(e, employee)}
                                          @dragenter=${this._dragenter}
                                          @dragleave=${this._dragleave}
                                          @dragend=${this._dragend}
                                      ></ptc-employees-documents-row>
                                  `
                              )}
                          </ptc-scroller>
                      `}

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
